<script lang="ts">
  import { Confetti } from "svelte-confetti"
  import { Trophy } from "lucide-svelte"
  import { Button } from "$lib/components/ui/button"
  import { t } from "$lib/utils/i18n"
  import userStore from "$lib/stores/userStore"
  import { generateHmac } from "$lib/utils/encryption"
  import { PUBLIC_HMAC_SECRET_KEY } from "$env/static/public"

  const { action, processAction, isPreview, competition } = $props()

  function getCouponData(action: any) {
    try {
      return JSON.parse(
        (action.selectedAction || action)?.requirements?.find(
          (r: any) => r.name === "Coupon Wheel Weights",
        )?.value,
      )
    } catch (err) {
      return []
    }
  }

  const coupons = $derived(getCouponData(action))

  let description = $derived(
    (action.selectedAction || action)?.requirements.find(
      (r) => r.name === "Instructions",
    )?.value,
  )

  // State for the wheel
  let isSpinning = $state(false)
  let showConfetti = $state(false)
  let currentWinner = $state<{ code: string; discount: string } | null>(null)
  let wheelElement: SVGSVGElement
  let previousEndDegree = 0
  let wheelContainer: HTMLDivElement
  let rotation = 0

  // Helper functions for the wheel
  function polarToCartesian(
    centerX: number,
    centerY: number,
    radius: number,
    angleInDegrees: number,
  ) {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    }
  }

  function describeArc(
    x: number,
    y: number,
    radius: number,
    startAngle: number,
    endAngle: number,
  ) {
    const start = polarToCartesian(x, y, radius, endAngle)
    const end = polarToCartesian(x, y, radius, startAngle)
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1"
    return [
      "M",
      x,
      y,
      "L",
      start.x,
      start.y,
      "A",
      radius,
      radius,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y,
      "Z",
    ].join(" ")
  }

  // Function to create wheel segments
  function createWheel() {
    if (!coupons.length) return []

    // Reset current angle to 0 and distribute 360 degrees among coupons
    let currentAngle = 0
    return coupons.map((coupon, i) => {
      // Calculate angle based on percentage
      const segmentAngle = (360 * coupon.percentage) / 100

      const segment = {
        code: coupon.code,
        discount: coupon.discount,
        percentage: coupon.percentage,
        startAngle: currentAngle,
        angle: segmentAngle,
        color: `hsl(${(i * 137.5) % 360}, 70%, 80%)`,
      }

      // Update current angle for next segment
      currentAngle += segmentAngle
      return segment
    })
  }

  // Function to get random degrees
  async function getRandomDegrees() {
    try {
      // Generate a random number between 1800 (5 rotations) and 2520 (7 rotations)
      return 1800 + Math.floor(Math.random() * 720)
    } catch (error) {
      console.error("Error generating random number:", error)
      return 1800 + Math.floor(Math.random() * 720)
    }
  }

  // Function to spin the wheel
  async function spin() {
    if (isSpinning || coupons.length === 0) return
    isSpinning = true
    currentWinner = null
    showConfetti = false

    // Get random degrees
    const randomAdditionalDegrees = await getRandomDegrees()
    const newEndDegree = previousEndDegree + randomAdditionalDegrees

    const animation = wheelElement.animate(
      [
        { transform: `rotate(${previousEndDegree}deg)` },
        { transform: `rotate(${newEndDegree}deg)` },
      ],
      {
        duration: 3000,
        easing: "cubic-bezier(0.440, -0.205, 0.000, 1.130)",
        fill: "forwards",
      },
    )

    animation.onfinish = () => {
      isSpinning = false
      const finalRotation = newEndDegree % 360
      const adjustedRotation = (360 - finalRotation) % 360

      const segments = createWheel()
      const winningSegment = segments.find((segment) => {
        return (
          adjustedRotation >= segment.startAngle &&
          adjustedRotation < segment.startAngle + segment.angle
        )
      })

      if (winningSegment) {
        currentWinner = {
          code: winningSegment.code,
          discount: winningSegment.discount,
        }
        showConfetti = true
      }
    }

    previousEndDegree = newEndDegree
  }

  // Format text for display in wheel
  function formatText(text: string) {
    if (text.length > 15) {
      return text.substring(0, 12) + "..."
    }
    return text
  }

  async function completeAction() {
    console.log("Complete action, fire off email at this point.")
    if (!isPreview) {
      const verificationData = currentWinner

      // Process the action first
      processAction(action, verificationData)

      // If we have coupon data, send email
      if (verificationData && verificationData.code) {
        try {
          await sendCouponEmail(verificationData)
        } catch (error) {
          console.error("Failed to send coupon email:", error)
          // Don't block the user flow if email fails
        }
      }
    }
  }

  async function sendCouponEmail(verificationData: {
    code: string
    discount: string
  }) {
    // We need to get these from parent components - let's add them as props
    if (!competition || !$userStore?.user) {
      console.error("Missing required data for sending coupon email")
      return
    }

    try {
      const requestBody = JSON.stringify({
        teamId: competition.teamId,
        userEmail: $userStore.user.email,
        verificationData,
        participantName: $userStore.user.name,
        requirements:
          action.requirements || action.selectedAction?.requirements,
      })

      // Generate HMAC signature if public key is available
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      }

      if (PUBLIC_HMAC_SECRET_KEY) {
        const hmacSignature = await generateHmac(
          requestBody,
          PUBLIC_HMAC_SECRET_KEY,
        )
        headers["x-hmac-signature"] = hmacSignature
      }

      const response = await fetch("/api/competitions/send-coupon-email", {
        method: "POST",
        headers,
        body: requestBody,
      })

      if (!response.ok) {
        const error = await response.json()
        console.error("Email API error:", error)
      } else {
        console.log("Coupon email sent successfully")
      }
    } catch (error) {
      console.error("Error calling email API:", error)
    }
  }
</script>

<div class="coupon-wheel-container">
  <div class="flex flex-col items-center justify-center">
    {#if showConfetti}
      <Confetti />
    {/if}

    {#if description}
      <div class="description-content text-black mb-10">
        {@html description}
      </div>
    {/if}

    <div class="relative w-[300px] h-[300px] mb-4" bind:this={wheelContainer}>
      <svg
        bind:this={wheelElement}
        width="300"
        height="300"
        viewBox="0 0 400 400"
      >
        <g transform="rotate({rotation}, 200, 200)">
          {#each createWheel() as segment}
            <path
              d={describeArc(
                200,
                200,
                190,
                segment.startAngle,
                segment.startAngle + segment.angle,
              )}
              fill={segment.color}
              stroke="white"
              stroke-width="1"
            />
            {#if segment.angle > 5}
              <text
                x="200"
                y="200"
                transform={`
                  rotate(${segment.startAngle + segment.angle / 2 - 90}, 200, 200)
                  translate(70, 0)
                `}
                text-anchor="start"
                dominant-baseline="middle"
                font-size={segment.angle < 10
                  ? "10"
                  : segment.angle < 20
                    ? "12"
                    : "14"}
                class="font-medium"
              >
                {formatText(segment.discount)}
              </text>
            {/if}
          {/each}
        </g>

        <!-- Center circle -->
        <circle
          cx="200"
          cy="200"
          r="12"
          fill="white"
          stroke="#ccc"
          stroke-width="2"
        />
      </svg>

      <!-- Pointer -->
      <div class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-20">
        <div
          class="w-0 h-0 border-l-[15px] border-l-transparent border-r-[15px] border-r-transparent border-t-[30px] border-t-red-500"
        ></div>
      </div>
    </div>

    {#if currentWinner && !isSpinning}
      <div class="text-xl font-bold text-center mt-4 animate-bounce">
        {t("widget_you_won")}
        {currentWinner.discount}! 🎉
      </div>
      <div class="text-md text-center mt-2">
        {t("widget_coupon_code")}:
        <span class="font-bold">{currentWinner.code}</span>
      </div>
    {/if}

    {#if currentWinner}
      <Button
        onclick={completeAction}
        class="w-auto text-center py-3 px-6 rounded-lg mb-6  text-white mt-4"
      >
        {t("widget_continue")}
      </Button>
    {:else}
      <Button
        disabled={isSpinning || coupons.length === 0}
        onclick={spin}
        class="w-auto text-center py-3 px-6 rounded-lg mb-6 bg-purple-500 hover:bg-purple-600 text-white mt-4"
      >
        <Trophy size={20} class="mr-2" />
        {t("widget_spin_to_win")}
      </Button>
    {/if}
  </div>
</div>

<style>
  @keyframes bounce {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  .animate-bounce {
    animation: bounce 1s ease infinite;
  }

  .coupon-wheel-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }
</style>
